import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserRole, formatMoney } from '@teentack/shared';
import { useAuth } from '@/auth/use-auth';
import { useJob, jobKeys } from '@/features/jobs/use-jobs';
import { publishJob } from '@/features/jobs/jobs.api';
import { useToggleShortlist } from '@/features/shortlist/use-shortlist';
import { useRequestConsent } from '@/features/consent/use-consent';
import { useSubmitApplication } from '@/features/applications/use-applications';
import { PageContainer } from '@/components/PageContainer';
import { ErrorNote, InfoNote } from '@/components/Form';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import {
  ArrowRightIcon,
  MapPinIcon,
  ShieldCheckIcon,
  UsersIcon,
  WalletIcon,
} from '@/components/ui/Icons';
import { BidPanel } from '@/features/bids/BidPanel';
import { ShareWithTeenButton } from '@/features/shortlist/ShareWithTeenButton';
import { presentApiError } from '@/lib/form-errors';
import { TrustBadge } from '@/features/connections/TrustBadge';

/**
 * One job in full. [Teen step 7 detail, Job Poster step 11]
 *
 * The same route serves both sides: a teen gets the action their consent state
 * allows, the owner gets the state of their own listing and a way into the
 * applicants. A draft is only ever visible to its owner — enforced by the api.
 */
export function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const jobQuery = useJob(jobId);
  const shortlist = useToggleShortlist();
  const consent = useRequestConsent();
  const apply = useSubmitApplication();

  const [coverNote, setCoverNote] = useState('');
  const [showApply, setShowApply] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const publish = useMutation({
    mutationFn: () => publishJob(jobId as string),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: jobKeys.all }),
  });

  async function run(action: () => Promise<unknown>, success: string): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
    } catch (caught) {
      setError(presentApiError(caught, 'Something went wrong').message);
    }
  }

  if (jobQuery.isPending) {
    return (
      <PageContainer>
        <Skeleton className="h-64" radius="xl" />
      </PageContainer>
    );
  }

  if (jobQuery.isError || !jobQuery.data) {
    return (
      <PageContainer width="narrow">
        <div className="rounded-2xl border border-line bg-white p-8 text-center shadow-soft">
          <h1 className="text-xl font-extrabold text-ink">Job not found</h1>
          <p className="mt-2 text-sm text-slate-500">
            It may have been closed, or it is still a draft.
          </p>
          <Button className="mt-6" onClick={() => navigate('/jobs')}>
            Back to opportunities
          </Button>
        </div>
      </PageContainer>
    );
  }

  const job = jobQuery.data;
  const isTeen = user?.role === UserRole.TEEN;
  /**
   * Only a minor needs a guardian's permission. An adult teen has no consent
   * record at all, so gating on `consentStatus === 'APPROVED'` locked them out
   * of both Apply and Bid.
   */
  const needsConsent = user?.isMinor ?? false;
  const mayEngage = !needsConsent || job.consentStatus === 'APPROVED';
  const isOwner = user?.role === UserRole.JOB_POSTER && job.posterName !== undefined;
  const isParent = user?.role === UserRole.PARENT;
  const slotsLeft = job.teensNeeded - job.teensAssigned;
  const busy = shortlist.isPending || consent.isPending || apply.isPending || publish.isPending;

  return (
    <PageContainer>
      <Link to="/jobs" className="text-sm font-semibold text-brand-600 hover:underline">
        ← All opportunities
      </Link>

      <div className="mt-4 grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start">
        {/* Main */}
        <div className="space-y-6">
          <header className="rounded-3xl border border-line bg-white p-6 shadow-lift sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <Badge tone="brand">{job.category}</Badge>
              <div className="flex flex-wrap gap-2">
                {job.status !== 'LIVE' && <Badge tone="neutral">{job.status}</Badge>}
                {job.needType === 'MULTIPLE_TEENS' && (
                  <Badge tone="accent">
                    {slotsLeft} of {job.teensNeeded} places left
                  </Badge>
                )}
              </div>
            </div>

            <h1 className="mt-4 text-2xl font-extrabold text-ink sm:text-3xl">{job.title}</h1>

            <p className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
              <span className="font-semibold text-slate-600">{job.posterName}</span>
              {(job.location || job.city) && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPinIcon className="size-4 text-slate-400" />
                  {job.location ?? [job.city, job.state].filter(Boolean).join(', ')}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 text-base font-bold text-ink">
                <WalletIcon className="size-4 text-safe-600" />
                {formatMoney(job.pay.amountMinor, job.pay.currency)}
                <span className="text-xs font-medium text-slate-400">
                  {job.payType === 'BID' ? 'open to bids' : 'per teen'}
                </span>
              </span>
            </p>
          </header>

          <section className="rounded-2xl border border-line bg-white p-6 shadow-soft">
            <h2 className="text-base font-bold text-ink">What needs doing</h2>
            <p className="mt-3 text-sm leading-relaxed whitespace-pre-line text-slate-600">
              {job.description}
            </p>

            {job.skills.length > 0 && (
              <>
                <h3 className="mt-6 text-sm font-bold text-ink">Skills wanted</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {job.skills.map((skill) => (
                    <Badge key={skill.publicId} tone="neutral">
                      {skill.name}
                    </Badge>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>

        {/* Action rail */}
        <aside className="space-y-4 lg:sticky lg:top-24">
          <div className="rounded-2xl border border-line bg-white p-6 shadow-soft">
            <ErrorNote message={error} />
            {notice && <InfoNote>{notice}</InfoNote>}

            {/* Why this employer can be trusted, before any action. */}
            <div className="mb-4">
              <TrustBadge trust={job.trust} tone="block" />
            </div>

            {isTeen && job.payType === 'BID' && (
              <div className="space-y-3">
                <BidPanel job={job} consentApproved={mayEngage} />

                {needsConsent && !mayEngage && job.consentStatus !== 'PENDING' && (
                  <Button
                    block
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () => consent.mutateAsync({ jobId: job.id, consentType: 'APPLY_CONSENT' }),
                        'Asked your guardian. You can bid once they approve.',
                      )
                    }
                  >
                    {job.consentStatus === 'DECLINED' ? 'Ask again' : 'Ask my guardian'}
                  </Button>
                )}

                {needsConsent && job.consentStatus === 'PENDING' && (
                  <p className="flex items-start gap-2 rounded-xl border border-sun-200 bg-sun-50 px-3.5 py-3 text-sm font-medium text-sun-700">
                    <ShieldCheckIcon className="mt-0.5 size-4 shrink-0" />
                    Waiting for your guardian to approve this job.
                  </p>
                )}

                <Button
                  variant="outline"
                  block
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () => shortlist.mutateAsync({ jobId: job.id, saved: job.savedByViewer }),
                      job.savedByViewer ? 'Removed from your shortlist.' : 'Saved.',
                    )
                  }
                >
                  {job.savedByViewer ? 'Remove from saved' : 'Save for later'}
                </Button>
              </div>
            )}

            {isTeen && job.payType !== 'BID' && (
              <div className="space-y-3">
                {job.appliedByViewer ? (
                  <>
                    <Badge tone="safe">You have applied</Badge>
                    <Link
                      to="/my-work"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 hover:underline"
                    >
                      Track it in My work
                      <ArrowRightIcon className="size-4" />
                    </Link>
                  </>
                ) : mayEngage ? (
                  showApply ? (
                    <>
                      <Textarea
                        label="Why you?"
                        value={coverNote}
                        onChange={(event) => setCoverNote(event.target.value)}
                        optional
                        maxLength={1000}
                        placeholder="A line or two about why you would be good at this."
                      />
                      <Button
                        block
                        disabled={busy}
                        onClick={() =>
                          void run(
                            () =>
                              apply.mutateAsync({
                                jobId: job.id,
                                ...(coverNote.trim() ? { coverNote: coverNote.trim() } : {}),
                              }),
                            'Application sent.',
                          )
                        }
                      >
                        {busy ? 'Sending…' : 'Send application'}
                      </Button>
                      <Button variant="ghost" block onClick={() => setShowApply(false)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button block onClick={() => setShowApply(true)}>
                      Apply for this job
                    </Button>
                  )
                ) : job.consentStatus === 'PENDING' ? (
                  <p className="flex items-start gap-2 rounded-xl border border-sun-200 bg-sun-50 px-3.5 py-3 text-sm font-medium text-sun-700">
                    <ShieldCheckIcon className="mt-0.5 size-4 shrink-0" />
                    Waiting for your guardian to approve this job.
                  </p>
                ) : (
                  <>
                    <p className="text-sm leading-relaxed text-slate-600">
                      Your guardian has to approve this job before you can apply.
                    </p>
                    <Button
                      block
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () =>
                            consent.mutateAsync({ jobId: job.id, consentType: 'APPLY_CONSENT' }),
                          'Asked your guardian. You can apply once they approve.',
                        )
                      }
                    >
                      {job.consentStatus === 'DECLINED' ? 'Ask again' : 'Ask my guardian'}
                    </Button>
                  </>
                )}

                <Button
                  variant="outline"
                  block
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () => shortlist.mutateAsync({ jobId: job.id, saved: job.savedByViewer }),
                      job.savedByViewer ? 'Removed from your shortlist.' : 'Saved.',
                    )
                  }
                >
                  {job.savedByViewer ? 'Remove from saved' : 'Save for later'}
                </Button>
              </div>
            )}

            {/* A guardian browsing can put this in front of their teen. */}
            {isParent && (
              <div className="space-y-3">
                <ShareWithTeenButton jobId={job.id} />
              </div>
            )}

            {isOwner && (
              <div className="space-y-3">
                {job.status === 'DRAFT' && (
                  <Button
                    block
                    disabled={busy}
                    onClick={() => void run(() => publish.mutateAsync(), 'Job published.')}
                  >
                    {publish.isPending ? 'Publishing…' : 'Publish this job'}
                  </Button>
                )}
                <Link
                  to={`/applicants?jobId=${job.id}`}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-line bg-white px-5 py-2.5 text-sm font-semibold text-ink transition hover:border-brand-300 hover:bg-brand-50"
                >
                  <UsersIcon className="size-4" />
                  View applicants
                </Link>
              </div>
            )}
          </div>

          <dl className="rounded-2xl border border-line bg-white p-6 text-sm shadow-soft">
            <div className="flex justify-between border-b border-line py-2 first:pt-0">
              <dt className="font-semibold text-slate-500">Teens needed</dt>
              <dd className="font-medium text-ink">{job.teensNeeded}</dd>
            </div>
            <div className="flex justify-between border-b border-line py-2">
              <dt className="font-semibold text-slate-500">Filled</dt>
              <dd className="font-medium text-ink">{job.teensAssigned}</dd>
            </div>
            <div className="flex justify-between py-2 last:pb-0">
              <dt className="font-semibold text-slate-500">Posted</dt>
              <dd className="font-medium text-ink">
                {(job.publishedAt ?? job.createdAt).slice(0, 10)}
              </dd>
            </div>
          </dl>
        </aside>
      </div>
    </PageContainer>
  );
}
